import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface FunnelStep {
  label: string;
  description: string;
  icon: string;
  count: number;
  dropoff: number;
  dropoffPct: number;
  color: string;
}

export default function FunnelDropoff() {
  const [steps, setSteps] = useState<FunnelStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSessions, setTotalSessions] = useState(0);

  useEffect(() => {
    supabase
      .from('page_events')
      .select('session_id, event_type')
      .then(({ data }) => {
        if (!data) { setLoading(false); return; }

        const sessions = new Map<string, Set<string>>();
        (data as Array<{ session_id: string; event_type: string }>).forEach((ev) => {
          if (!sessions.has(ev.session_id)) sessions.set(ev.session_id, new Set());
          sessions.get(ev.session_id)!.add(ev.event_type);
        });

        const total = sessions.size;
        setTotalSessions(total);

        const counts = {
          entered: 0,
          scrolled50: 0,
          clickedBtn: 0,
          openedForm: 0,
          reachedStep2: 0,
          submitted: 0,
        };

        sessions.forEach((evTypes) => {
          if (evTypes.has('page_enter')) counts.entered++;
          if (evTypes.has('scroll_depth')) counts.scrolled50++;
          if (evTypes.has('button_click')) counts.clickedBtn++;
          if (evTypes.has('modal_open')) counts.openedForm++;
          if (evTypes.has('modal_step2')) counts.reachedStep2++;
          if (evTypes.has('whatsapp_submit')) counts.submitted++;
        });

        const raw = [
          { label: 'Siteye Girdi', description: 'Sayfayı açtı', icon: 'ri-login-circle-line', count: counts.entered, color: '#a78bfa' },
          { label: 'Sayfayı Gezdi', description: 'En az %25 scroll etti', icon: 'ri-arrow-down-double-line', count: counts.scrolled50, color: '#60a5fa' },
          { label: 'Butona Bastı', description: '"Demo Talep Et" veya başka CTA', icon: 'ri-cursor-line', count: counts.clickedBtn, color: '#00d4ff' },
          { label: 'Formu Açtı', description: 'WhatsApp formunu açtı', icon: 'ri-window-2-line', count: counts.openedForm, color: '#f0b429' },
          { label: 'Adım 2\'ye Geçti', description: 'Sektör bilgisini girdi', icon: 'ri-arrow-right-circle-line', count: counts.reachedStep2, color: '#fb923c' },
          { label: 'Lead Oldu', description: 'Formu gönderdi', icon: 'ri-whatsapp-line', count: counts.submitted, color: '#4ade80' },
        ];

        const result: FunnelStep[] = raw.map((step, i) => {
          const prev = i === 0 ? step.count : raw[i - 1].count;
          const dropoff = prev - step.count;
          const dropoffPct = prev > 0 ? Math.round((dropoff / prev) * 100) : 0;
          return { ...step, dropoff, dropoffPct };
        });

        setSteps(result);
        setLoading(false);
      });
  }, []);

  const maxCount = steps[0]?.count || 1;

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold text-sm">Dönüşüm Hunisi</h3>
          <p className="text-gray-500 text-xs mt-0.5">Ziyaretçilerin hangi adımda kaybolduğu</p>
        </div>
        <div
          className="px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: 'rgba(240,180,41,0.1)', color: '#f0b429', border: '1px solid rgba(240,180,41,0.2)' }}
        >
          {totalSessions} toplam oturum
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#f0b429' }} />
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((step, i) => {
            const widthPct = maxCount > 0 ? (step.count / maxCount) * 100 : 0;
            const isWorstDropoff = i > 0 && step.dropoffPct === Math.max(...steps.slice(1).map((s) => s.dropoffPct));

            return (
              <div key={step.label}>
                {/* Step row */}
                <div className="flex items-center gap-3">
                  {/* Step number + icon */}
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: `${step.color}18`, border: `1px solid ${step.color}30` }}
                  >
                    <i className={`${step.icon} text-sm`} style={{ color: step.color }} />
                  </div>

                  {/* Bar + label */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-semibold">{step.label}</span>
                        <span className="text-gray-600 text-[10px]">{step.description}</span>
                      </div>
                      <span className="text-white text-xs font-bold flex-shrink-0 ml-2">{step.count}</span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${widthPct}%`,
                          background: `linear-gradient(90deg, ${step.color}cc, ${step.color})`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Dropoff indicator */}
                {i > 0 && step.dropoff > 0 && (
                  <div
                    className="flex items-center gap-2 ml-11 mt-1 mb-1 px-2.5 py-1 rounded-lg"
                    style={{
                      background: isWorstDropoff ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isWorstDropoff ? 'rgba(248,113,113,0.2)' : 'rgba(255,255,255,0.04)'}`,
                    }}
                  >
                    <i
                      className="ri-arrow-down-line text-[10px]"
                      style={{ color: isWorstDropoff ? '#f87171' : '#4b5563' }}
                    />
                    <span
                      className="text-[10px] font-semibold"
                      style={{ color: isWorstDropoff ? '#f87171' : '#6b7280' }}
                    >
                      {step.dropoff} kişi burada ayrıldı
                      {isWorstDropoff && ' ← En büyük kayıp noktası!'}
                    </span>
                    <span
                      className="text-[10px] font-bold ml-auto"
                      style={{ color: isWorstDropoff ? '#f87171' : '#6b7280' }}
                    >
                      -%{step.dropoffPct}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Conversion rate summary */}
      {!loading && steps.length > 0 && (
        <div
          className="mt-5 flex items-center justify-between px-4 py-3 rounded-xl"
          style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)' }}
        >
          <div className="flex items-center gap-2">
            <i className="ri-percent-line text-green-400 text-sm" />
            <span className="text-green-400 text-xs font-bold">Toplam Dönüşüm Oranı</span>
          </div>
          <span className="text-green-400 text-lg font-black">
            %{steps[0].count > 0 ? Math.round((steps[steps.length - 1].count / steps[0].count) * 100) : 0}
          </span>
        </div>
      )}
    </div>
  );
}
